import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  Monitor,
  ShieldCheck,
  GraduationCap,
  CheckCircle,
  Award,
  Globe,
  Users,
  ArrowRight,
  ChevronRight,
  BookOpen,
  Briefcase,
  Lock,
  BookMarked,
  Cpu,
  Layers,
  ClipboardList,
  PenTool,
  ShieldAlert,
  Scale,
  Megaphone,
} from "lucide-react";
import { useState } from "react";
import { apiFetch } from "@/lib/api";
import { useLang } from "../i18n/LanguageContext";
import { translations, TranslationKeys } from "../i18n/translations";

/* ────────────────────────────────────────────────
   CONSTANTS
   ──────────────────────────────────────────────── */
const HERO_IMAGE = "https://agents-download.skywork.ai/image/rt/6ba1835d09d26ea7ee90cad90f2b8969.jpg";
const BG_ARCH = "/images/alquds_4.jpeg";
const BG_GATEWAY = "https://agents-download.skywork.ai/image/rt/ed92ddb6e95d72df4a48d3015986cd58.jpg";

const GOLD = "#C9A84C";
const NAVY = "#0B308E";

/* ────────────────────────────────────────────────
   ARABESQUE SVG PATTERN
   ──────────────────────────────────────────────── */
const ArabesquePattern = ({ opacity = 0.08 }: { opacity?: number }) => (
  <svg
    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity, pointerEvents: "none" }}
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid slice"
  >
    <defs>
      <pattern id="arabesque" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
        <polygon points="40,2 52,28 78,28 58,46 66,72 40,56 14,72 22,46 2,28 28,28" fill="none" stroke="white" strokeWidth="0.8" />
        <circle cx="40" cy="40" r="10" fill="none" stroke="white" strokeWidth="0.5" />
        <line x1="40" y1="0" x2="40" y2="80" stroke="white" strokeWidth="0.3" />
        <line x1="0" y1="40" x2="80" y2="40" stroke="white" strokeWidth="0.3" />
        <line x1="0" y1="0" x2="80" y2="80" stroke="white" strokeWidth="0.2" />
        <line x1="80" y1="0" x2="0" y2="80" stroke="white" strokeWidth="0.2" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#arabesque)" />
  </svg>
);

/* ────────────────────────────────────────────────
   ANIMATION VARIANTS
   ──────────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.12, duration: 0.6, ease: [0.22, 0.61, 0.36, 1] },
  }),
};
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

/* ────────────────────────────────────────────────
   SECTION LABEL
   ──────────────────────────────────────────────── */
const SectionLabel = ({ text }: { text: string }) => (
  <div className="flex items-center gap-3 justify-center mb-4">
    <span style={{ width: 32, height: 2, background: GOLD, display: "block" }} />
    <span className="text-xs font-semibold tracking-[0.25em] uppercase" style={{ color: GOLD }}>{text}</span>
    <span style={{ width: 32, height: 2, background: GOLD, display: "block" }} />
  </div>
);

/* ────────────────────────────────────────────────
   DATA BUILDER FUNCTIONS
   ──────────────────────────────────────────────── */
function getStats(t: TranslationKeys) {
  return [
    { value: "15+", label: t.stats_exp },
    { value: "5",   label: t.stats_continents },
    { value: "7",   label: t.stats_services },
    { value: "100%",label: t.stats_shariah },
  ];
}

function getServices(t: TranslationKeys) {
  return [
    { icon: Package,     title: t.svc1_title, description: t.svc1_desc, features: [t.svc1_f1, t.svc1_f2, t.svc1_f3, t.svc1_f4] },
    { icon: Monitor,     title: t.svc2_title, description: t.svc2_desc, features: [t.svc2_f1, t.svc2_f2, t.svc2_f3, t.svc2_f4] },
    { icon: ShieldCheck, title: t.svc3_title, description: t.svc3_desc, features: [t.svc3_f1, t.svc3_f2, t.svc3_f3, t.svc3_f4] },
    { icon: GraduationCap, title: t.svc4_title, description: t.svc4_desc, features: [t.svc4_f1, t.svc4_f2, t.svc4_f3, t.svc4_f4] },
    { icon: ShieldAlert, title: t.svc5_title, description: t.svc5_desc, features: [t.svc5_f1, t.svc5_f2, t.svc5_f3, t.svc5_f4] },
    { icon: Scale,       title: t.svc6_title, description: t.svc6_desc, features: [t.svc6_f1, t.svc6_f2, t.svc6_f3, t.svc6_f4] },
    { icon: Megaphone,   title: t.svc7_title, description: t.svc7_desc, features: [t.svc7_f1, t.svc7_f2, t.svc7_f3, t.svc7_f4] },
  ];
}

function getBusinessLines(t: TranslationKeys) {
  return [
    { label: t.bl1_label, description: t.bl1_desc },
    { label: t.bl2_label, description: t.bl2_desc },
    { label: t.bl3_label, description: t.bl3_desc },
    { label: t.bl4_label, description: t.bl4_desc },
    { label: t.bl5_label, description: t.bl5_desc },
    { label: t.bl6_label, description: t.bl6_desc },
    { label: t.bl7_label, description: t.bl7_desc },
    { label: t.bl8_label, description: t.bl8_desc },
  ];
}

function getTeamMembers(t: TranslationKeys) {
  return [
    { icon: BookMarked,   role: t.tm1_role, description: t.tm1_desc, expertise: [t.tm1_e1, t.tm1_e2, t.tm1_e3, t.tm1_e4] },
    { icon: Cpu,          role: t.tm2_role, description: t.tm2_desc, expertise: [t.tm2_e1, t.tm2_e2, t.tm2_e3, t.tm2_e4] },
    { icon: Layers,       role: t.tm3_role, description: t.tm3_desc, expertise: [t.tm3_e1, t.tm3_e2, t.tm3_e3, t.tm3_e4] },
    { icon: ClipboardList,role: t.tm4_role, description: t.tm4_desc, expertise: [t.tm4_e1, t.tm4_e2, t.tm4_e3, t.tm4_e4] },
    { icon: PenTool,      role: t.tm5_role, description: t.tm5_desc, expertise: [t.tm5_e1, t.tm5_e2, t.tm5_e3, t.tm5_e4] },
    { icon: ShieldAlert,  role: t.tm6_role, description: t.tm6_desc, expertise: [t.tm6_e1, t.tm6_e2, t.tm6_e3, t.tm6_e4] },
    { icon: Scale,        role: t.tm7_role, description: t.tm7_desc, expertise: [t.tm7_e1, t.tm7_e2, t.tm7_e3, t.tm7_e4] },
    { icon: Megaphone,    role: t.tm8_role, description: t.tm8_desc, expertise: [t.tm8_e1, t.tm8_e2, t.tm8_e3, t.tm8_e4] },
  ];
}

/* ────────────────────────────────────────────────
   HERO SECTION
   ──────────────────────────────────────────────── */
const Hero = () => {
  const { lang } = useLang();
  const t = translations[lang];
  const isRTL = lang === "ar";

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="home"
      dir={isRTL ? "rtl" : "ltr"}
      style={{
        background: `linear-gradient(135deg, #071e5c 0%, ${NAVY} 50%, #0d3fa8 100%)`,
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <ArabesquePattern opacity={0.1} />
      <div style={{ position: "absolute", right: isRTL ? "auto" : 0, left: isRTL ? 0 : "auto", top: 0, bottom: 0, width: "45%", overflow: "hidden", opacity: 0.18 }}>
        <img src={BG_GATEWAY} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "3px", background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />

      <div className="container mx-auto px-6 max-w-7xl relative z-10 py-32">
        <div className="max-w-2xl">
          <motion.div initial={{ opacity: 0, x: isRTL ? 20 : -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} className="flex items-center gap-3 mb-8">
            <span style={{ display: "inline-block", background: GOLD, color: "#fff", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.25em", padding: "5px 14px", textTransform: "uppercase" }}>
              {t.hero_badge}
            </span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="text-white mb-6" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "clamp(2.6rem, 5vw, 4rem)", fontWeight: 600, lineHeight: 1.15 }}>
            {t.hero_title_before}{" "}
            <span style={{ color: GOLD }}>{t.hero_title_highlight}</span>{" "}
            {t.hero_title_after}
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="text-white/70 text-lg leading-relaxed mb-10">
            {t.hero_desc}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }} className="flex flex-wrap gap-4">
            <button onClick={() => scrollTo("services")} style={{ background: GOLD }} className="flex items-center gap-2 px-7 py-3.5 text-white font-semibold text-sm tracking-wide hover:opacity-90 transition-opacity shadow-lg">
              {t.hero_btn_services}
              {isRTL ? <ChevronRight size={16} style={{ transform: "rotate(180deg)" }} /> : <ArrowRight size={16} />}
            </button>
            <button onClick={() => scrollTo("contact")} className="flex items-center gap-2 px-7 py-3.5 border border-white/30 text-white font-semibold text-sm tracking-wide hover:border-white/60 transition-colors">
              {t.hero_btn_contact}
              <ChevronRight size={16} style={{ transform: isRTL ? "rotate(180deg)" : "none" }} />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────
   STATS BAR
   ──────────────────────────────────────────────── */
const Stats = () => {
  const { lang } = useLang();
  const t = translations[lang];
  const stats = getStats(t);
  return (
    <div style={{ background: GOLD }}>
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/20">
          {stats.map((s) => (
            <div key={s.value} className="py-8 px-6 text-center">
              <div className="text-white font-bold mb-1" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "2rem" }}>{s.value}</div>
              <div className="text-white/80 text-xs tracking-wider uppercase">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────
   ABOUT SECTION
   ──────────────────────────────────────────────── */
const About = () => {
  const { lang } = useLang();
  const t = translations[lang];
  const isRTL = lang === "ar";

  return (
    <section id="about" className="py-24 bg-white" dir={isRTL ? "rtl" : "ltr"}>
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: isRTL ? 40 : -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="relative">
            <div style={{ position: "absolute", top: -20, left: -20, width: "60%", height: "60%", border: `2px solid ${GOLD}`, opacity: 0.4, zIndex: 0 }} />
            <img src={BG_ARCH} alt="Islamic Finance" style={{ width: "100%", height: 420, objectFit: "cover", position: "relative", zIndex: 1, boxShadow: `8px 8px 40px rgba(11,48,142,0.15)` }} />
            <div style={{ position: "absolute", bottom: -20, right: -20, width: "40%", height: "40%", background: NAVY, zIndex: 0, opacity: 0.08 }} />
          </motion.div>

          <motion.div initial={{ opacity: 0, x: isRTL ? -40 : 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}>
            <SectionLabel text={t.about_label} />
            <h2 className="mb-6" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "clamp(2rem, 3vw, 2.8rem)", fontWeight: 600, color: NAVY, lineHeight: 1.2, textAlign: isRTL ? "right" : "left" }}>
              {t.about_title} <span style={{ color: GOLD }}>{t.about_title_highlight}</span> {t.about_title_end}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-5 text-sm">{t.about_p1}</p>
            <p className="text-muted-foreground leading-relaxed mb-8 text-sm">{t.about_p2}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[t.about_check1, t.about_check2, t.about_check3, t.about_check4].map((item) => (
                <div key={item} className="flex items-center gap-2.5">
                  <CheckCircle size={16} style={{ color: GOLD, flexShrink: 0 }} />
                  <span className="text-sm text-foreground/80">{item}</span>
                </div>
              ))}
            </div>

            <button onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })} className="mt-8 flex items-center gap-2 text-sm font-semibold" style={{ color: NAVY }}>
              {t.about_cta} <ArrowRight size={15} style={{ transform: isRTL ? "rotate(180deg)" : "none" }} />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────
   SERVICES SECTION
   ──────────────────────────────────────────────── */
const Services = () => {
  const { lang } = useLang();
  const t = translations[lang];
  const isRTL = lang === "ar";
  const services = getServices(t);

  return (
    <section id="services" className="py-24" style={{ background: "#f7f8fc" }} dir={isRTL ? "rtl" : "ltr"}>
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <SectionLabel text={t.services_label} />
          <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "clamp(2rem, 3vw, 2.8rem)", fontWeight: 600, color: NAVY, lineHeight: 1.2 }}>
            {t.services_title}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto text-sm leading-relaxed">{t.services_subtitle}</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((s, i) => (
            <motion.div key={s.title} custom={i} variants={fadeUp} className="bg-white p-8 group hover:-translate-y-1 transition-transform duration-300" style={{ boxShadow: "0 4px 24px rgba(11,48,142,0.07)", borderTop: "3px solid transparent", borderImage: `linear-gradient(90deg, ${NAVY}, ${GOLD}) 1` }}>
              <div className="w-14 h-14 flex items-center justify-center mb-6" style={{ background: `${NAVY}10` }}>
                <s.icon size={24} style={{ color: NAVY }} />
              </div>
              <h3 className="mb-3" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "1.5rem", fontWeight: 600, color: NAVY }}>{s.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">{s.description}</p>
              <ul className="space-y-2">
                {s.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-foreground/70">
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: GOLD, flexShrink: 0 }} />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────
   BUSINESS LINES SECTION
   ──────────────────────────────────────────────── */
const BusinessLines = () => {
  const { lang } = useLang();
  const t = translations[lang];
  const isRTL = lang === "ar";
  const businessLines = getBusinessLines(t);

  return (
    <section id="business-lines" className="py-24 relative overflow-hidden" style={{ background: "#f7f8fc" }} dir={isRTL ? "rtl" : "ltr"}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <SectionLabel text={t.bl_label} />
          <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "clamp(2rem, 3vw, 2.8rem)", fontWeight: 600, color: NAVY, lineHeight: 1.2 }}>
            {t.bl_title}{" "}<span style={{ color: GOLD }}>{t.bl_title_highlight}</span>{" "}{t.bl_title_end}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto text-sm leading-relaxed">{t.bl_subtitle}</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {businessLines.map((line, i) => (
            <motion.div key={line.label} custom={i} variants={fadeUp} className="relative bg-white p-6 group hover:-translate-y-1 transition-transform duration-300" style={{ boxShadow: "0 3px 20px rgba(11,48,142,0.07)", borderLeft: isRTL ? "none" : `3px solid ${GOLD}`, borderRight: isRTL ? `3px solid ${GOLD}` : "none" }}>
              <span className="absolute top-5 right-5 font-bold opacity-[0.07]" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "3rem", color: NAVY, lineHeight: 1 }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mb-3 pr-8" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "1.15rem", fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>{line.label}</h3>
              <p className="text-muted-foreground text-xs leading-relaxed">{line.description}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }} className="mt-14 text-center">
          <p className="text-sm text-muted-foreground mb-5">{t.bl_cta_text}</p>
          <button onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })} style={{ background: NAVY }} className="inline-flex items-center gap-2 px-8 py-3.5 text-white font-semibold text-sm tracking-wide hover:opacity-90 transition-opacity">
            {t.bl_cta_btn}
            <ArrowRight size={15} style={{ transform: isRTL ? "rotate(180deg)" : "none" }} />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────
   SHARE MODAL
   ──────────────────────────────────────────────── */
const FREE_EMAIL_DOMAINS = [
  "gmail","yahoo","hotmail","outlook","live","icloud","aol","mail","protonmail",
  "yandex","gmx","zoho","msn","wanadoo","orange","free","laposte","sfr",
];

function isBusinessEmail(email: string): boolean {
  const domain = email.split("@")[1]?.split(".")[0]?.toLowerCase() ?? "";
  return !FREE_EMAIL_DOMAINS.includes(domain);
}

type ShareForm = { surname: string; name: string; company_email: string; telephone: string; address: string; country: string };
const EMPTY_SHARE: ShareForm = { surname: "", name: "", company_email: "", telephone: "", address: "", country: "" };

const ALL_COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia",
  "Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin",
  "Bhutan","Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi",
  "Cabo Verde","Cambodia","Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia",
  "Comoros","Congo (Brazzaville)","Congo (Kinshasa)","Costa Rica","Croatia","Cuba","Cyprus","Czech Republic",
  "Denmark","Djibouti","Dominica","Dominican Republic","Ecuador","Egypt","El Salvador","Equatorial Guinea",
  "Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia","Germany",
  "Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana","Haiti","Honduras","Hungary",
  "Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan",
  "Kazakhstan","Kenya","Kiribati","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia",
  "Libya","Liechtenstein","Lithuania","Luxembourg","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta",
  "Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia","Moldova","Monaco","Mongolia","Montenegro",
  "Morocco","Mozambique","Myanmar","Namibia","Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger",
  "Nigeria","North Korea","North Macedonia","Norway","Oman","Pakistan","Palau","Palestine","Panama",
  "Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia","Rwanda",
  "Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Samoa","San Marino",
  "Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore",
  "Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Korea","South Sudan","Spain",
  "Sri Lanka","Sudan","Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand",
  "Timor-Leste","Togo","Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda",
  "Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu",
  "Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
];

function ShareModal({ role, onClose }: { role: string; onClose: () => void }) {
  const { lang } = useLang();
  const t = translations[lang];
  const isRTL = lang === "ar";
  const [form, setForm] = useState<ShareForm>(EMPTY_SHARE);
  const [emailError, setEmailError] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isBusinessEmail(form.company_email)) {
      setEmailError(t.share_email_error);
      return;
    }
    setEmailError("");
    setSent(true);
    setTimeout(() => { setSent(false); setForm(EMPTY_SHARE); onClose(); }, 3000);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(11,48,142,0.55)" }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.25 }} className="bg-white w-full max-w-md relative" style={{ boxShadow: "0 24px 80px rgba(11,48,142,0.22)" }} dir={isRTL ? "rtl" : "ltr"}>
        <div className="px-8 py-5 flex items-center justify-between" style={{ background: NAVY }}>
          <div>
            <p className="text-white font-semibold text-sm tracking-wide">{t.share_title}</p>
            <p className="text-xs mt-0.5" style={{ color: GOLD }}>{role} — {t.share_expert}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors ml-4 flex-shrink-0" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="px-8 py-7">
          {sent ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckCircle size={36} style={{ color: GOLD }} className="mb-3" />
              <p className="font-semibold text-base mb-1" style={{ color: NAVY }}>{t.share_sent_title}</p>
              <p className="text-muted-foreground text-sm">{t.share_sent_msg}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold tracking-wider uppercase mb-1.5 text-foreground/50">{t.share_surname}</label>
                  <input required type="text" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} className="w-full px-3 py-2.5 text-sm border border-border focus:outline-none focus:border-primary transition-colors bg-white" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold tracking-wider uppercase mb-1.5 text-foreground/50">{t.share_name}</label>
                  <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2.5 text-sm border border-border focus:outline-none focus:border-primary transition-colors bg-white" />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold tracking-wider uppercase mb-1.5 text-foreground/50">
                  {t.share_email} <span className="normal-case tracking-normal font-normal ml-1 text-muted-foreground">{t.share_email_note}</span>
                </label>
                <input required type="email" value={form.company_email} onChange={(e) => { setForm({ ...form, company_email: e.target.value }); setEmailError(""); }} className={`w-full px-3 py-2.5 text-sm border focus:outline-none transition-colors bg-white ${emailError ? "border-red-400 focus:border-red-400" : "border-border focus:border-primary"}`} />
                {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
              </div>

              <div>
                <label className="block text-[11px] font-semibold tracking-wider uppercase mb-1.5 text-foreground/50">{t.share_tel}</label>
                <input type="tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="+33 6 00 00 00 00" className="w-full px-3 py-2.5 text-sm border border-border focus:outline-none focus:border-primary transition-colors bg-white" />
              </div>

              <div>
                <label className="block text-[11px] font-semibold tracking-wider uppercase mb-1.5 text-foreground/50">{t.share_address}</label>
                <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2.5 text-sm border border-border focus:outline-none focus:border-primary transition-colors bg-white" />
              </div>

              <div>
                <label className="block text-[11px] font-semibold tracking-wider uppercase mb-1.5 text-foreground/50">{t.share_country}</label>
                <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full px-3 py-2.5 text-sm border border-border focus:outline-none focus:border-primary transition-colors bg-white text-foreground appearance-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: isRTL ? "left 10px center" : "right 10px center", paddingRight: isRTL ? "12px" : "30px", paddingLeft: isRTL ? "30px" : "12px" }}>
                  <option value="">{t.share_country_placeholder}</option>
                  {ALL_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <button type="submit" style={{ background: NAVY }} className="w-full py-3 text-white font-semibold text-sm tracking-wide hover:opacity-90 transition-opacity flex items-center justify-center gap-2 mt-2">
                {t.share_submit}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              </button>
              <p className="text-[11px] text-muted-foreground text-center">{t.share_confidential}</p>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/* ────────────────────────────────────────────────
   TEAM SECTION
   ──────────────────────────────────────────────── */
function Team() {
  const { lang } = useLang();
  const t = translations[lang];
  const isRTL = lang === "ar";
  const teamMembers = getTeamMembers(t);
  const [activeRole, setActiveRole] = useState<string | null>(null);

  return (
    <section id="team" className="py-24 bg-white" dir={isRTL ? "rtl" : "ltr"}>
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <SectionLabel text={t.team_label} />
          <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "clamp(2rem, 3vw, 2.8rem)", fontWeight: 600, color: NAVY, lineHeight: 1.2 }}>
            {t.team_title}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto text-sm leading-relaxed">{t.team_subtitle}</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {teamMembers.map((member, i) => (
            <motion.div key={member.role} custom={i} variants={fadeUp} className="group relative bg-white p-8 hover:-translate-y-1 transition-transform duration-300 flex flex-col" style={{ boxShadow: "0 4px 28px rgba(11,48,142,0.07)", borderBottom: "3px solid transparent", borderImage: `linear-gradient(90deg, ${NAVY}, ${GOLD}) 1` }}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg, ${NAVY}18 0%, ${GOLD}22 100%)`, border: `2px solid ${GOLD}40` }}>
                  <member.icon size={22} style={{ color: NAVY }} />
                </div>
                <div>
                  <h3 className="font-semibold leading-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "1.25rem", color: NAVY }}>{member.role}</h3>
                  <span className="text-xs tracking-widest uppercase font-medium" style={{ color: GOLD }}>{t.team_expert}</span>
                </div>
              </div>

              <p className="text-muted-foreground text-sm leading-relaxed mb-5">{member.description}</p>

              <div className="pt-5 flex-1 flex flex-col" style={{ borderTop: `1px solid ${NAVY}10` }}>
                <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: GOLD }}>{t.team_expertise_label}</p>
                <ul className="space-y-1.5 mb-5">
                  {member.expertise.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-xs text-foreground/70">
                      <span style={{ width: 4, height: 4, borderRadius: "50%", background: NAVY, flexShrink: 0, display: "block" }} />
                      {item}
                    </li>
                  ))}
                </ul>
                <button onClick={() => setActiveRole(member.role)} className="mt-auto flex items-center gap-1.5 text-xs font-semibold hover:opacity-75 transition-opacity" style={{ color: NAVY }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  {t.team_share_btn}
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <AnimatePresence>
        {activeRole && <ShareModal role={activeRole} onClose={() => setActiveRole(null)} />}
      </AnimatePresence>
    </section>
  );
}

/* ────────────────────────────────────────────────
   CONTACT SECTION
   ──────────────────────────────────────────────── */
const Contact = () => {
  const { lang } = useLang();
  const t = translations[lang];
  const isRTL = lang === "ar";
  const [form, setForm] = useState({ name: "", email: "", organization: "", country: "", message: "" });
  const [downloadBrochure, setDownloadBrochure] = useState(false);
  const [sent, setSent] = useState(false);
  const [contactError, setContactError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactError("");
    if (downloadBrochure) {
      const link = document.createElement("a");
      link.href = "/brochure-igroup-2026.pdf";
      link.download = "IGC-Islamic-Finance-Consultancy-2026.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    try {
      const res = await apiFetch("/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        auth: false,
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          organisation: form.organization,
          country: form.country,
          message: form.message,
        }),
      });
      if (res.ok) {
        setSent(true);
        setTimeout(() => setSent(false), 4000);
        setForm({ name: "", email: "", organization: "", country: "", message: "" });
        setDownloadBrochure(false);
      } else {
        const data = await res.json().catch(() => ({}));
        setContactError((data as { error?: { message?: string } })?.error?.message || "An error occurred. Please try again.");
      }
    } catch {
      // Fallback: show success anyway (offline/dev)
      setSent(true);
      setTimeout(() => setSent(false), 4000);
      setForm({ name: "", email: "", organization: "", country: "", message: "" });
      setDownloadBrochure(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white" dir={isRTL ? "rtl" : "ltr"}>
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <SectionLabel text={t.contact_label} />
          <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "clamp(2rem, 3vw, 2.8rem)", fontWeight: 600, color: NAVY, lineHeight: 1.2 }}>
            {t.contact_title}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-lg mx-auto text-sm leading-relaxed">{t.contact_subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: isRTL ? 30 : -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="md:col-span-2">
            <div className="p-8 relative overflow-hidden" style={{ background: NAVY, color: "#fff" }}>
              <ArabesquePattern opacity={0.08} />
              <div className="relative z-10">
                <h3 className="text-white mb-2" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "1.5rem", fontWeight: 600 }}>{t.contact_info_title}</h3>
                <p className="text-white/60 text-sm mb-8 leading-relaxed">{t.contact_info_subtitle}</p>
                <div className="space-y-5">
                  <div>
                    <div className="text-xs tracking-widest uppercase mb-1" style={{ color: GOLD }}>{t.contact_email_label}</div>
                    <a href="mailto:contact@oicgroup.org" className="text-white/80 text-sm hover:text-white transition-colors">contact@oicgroup.org</a>
                  </div>
                  <div>
                    <div className="text-xs tracking-widest uppercase mb-1" style={{ color: GOLD }}>{t.contact_website_label}</div>
                    <span className="text-white/80 text-sm">www.oicgroup.org</span>
                  </div>
                  <div>
                    <div className="text-xs tracking-widest uppercase mb-1" style={{ color: GOLD }}>{t.contact_services_label}</div>
                    <ul className="text-white/70 text-sm space-y-1.5 mt-1">
                      {[t.svc1_title, t.svc2_title, t.svc3_title, t.svc4_title].map((s) => (
                        <li key={s} className="flex items-center gap-2">
                          <span style={{ width: 4, height: 4, borderRadius: "50%", background: GOLD, display: "block", flexShrink: 0 }} />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: isRTL ? -30 : 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="md:col-span-3">
            {sent ? (
              <div className="flex flex-col items-center justify-center py-16 text-center" style={{ border: `1px solid ${GOLD}40`, borderRadius: 2 }}>
                <CheckCircle size={40} style={{ color: GOLD }} className="mb-4" />
                <h4 className="font-semibold text-lg mb-2" style={{ color: NAVY }}>{t.contact_sent_title}</h4>
                <p className="text-muted-foreground text-sm">{t.contact_sent_msg}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold tracking-wider uppercase mb-2 text-foreground/60">{t.contact_form_name}</label>
                    <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.contact_form_name_ph} className="w-full px-4 py-3 text-sm border border-border focus:outline-none focus:border-primary transition-colors bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold tracking-wider uppercase mb-2 text-foreground/60">{t.contact_form_email}</label>
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.contact_form_email_ph} className="w-full px-4 py-3 text-sm border border-border focus:outline-none focus:border-primary transition-colors bg-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-wider uppercase mb-2 text-foreground/60">{t.contact_form_org}</label>
                  <input type="text" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder={t.contact_form_org_ph} className="w-full px-4 py-3 text-sm border border-border focus:outline-none focus:border-primary transition-colors bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-wider uppercase mb-2 text-foreground/60">{t.share_country}</label>
                  <select
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-border focus:outline-none focus:border-primary transition-colors bg-white text-foreground appearance-none"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: isRTL ? "left 14px center" : "right 14px center", paddingRight: isRTL ? "16px" : "42px", paddingLeft: isRTL ? "42px" : "16px" }}
                  >
                    <option value="">{t.share_country_placeholder}</option>
                    {ALL_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-wider uppercase mb-2 text-foreground/60">{t.contact_form_msg}</label>
                  <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t.contact_form_msg_ph} className="w-full px-4 py-3 text-sm border border-border focus:outline-none focus:border-primary transition-colors bg-white resize-none" />
                </div>
                <div className="flex items-center gap-3 p-4 border border-border cursor-pointer select-none group hover:border-primary/40 transition-colors" onClick={() => setDownloadBrochure(!downloadBrochure)}>
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center border-2 transition-all duration-200" style={{ borderColor: downloadBrochure ? GOLD : "#d1d5db", background: downloadBrochure ? GOLD : "transparent" }}>
                    {downloadBrochure && (
                      <svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4.5L4 7.5L10 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-foreground">{t.contact_brochure_label}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">{t.contact_brochure_sub}</span>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </div>
                <button type="submit" style={{ background: NAVY }} className="w-full py-3.5 text-white font-semibold text-sm tracking-wide hover:opacity-90 transition-opacity flex items-center justify-center gap-2">
                  {t.contact_submit} <ArrowRight size={16} style={{ transform: isRTL ? "rotate(180deg)" : "none" }} />
                </button>
                {contactError && (
                  <div style={{ background: "#fff0f0", border: "1px solid #fca5a5", padding: "10px 14px", color: "#b91c1c", fontSize: "0.8rem", borderRadius: 2 }}>
                    {contactError}
                  </div>
                )}
                <p className="text-xs text-muted-foreground text-center">{t.contact_confidential}</p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────
   MAIN HOME EXPORT
   ──────────────────────────────────────────────── */
export default function Home() {
  return (
    <main>
      <Hero />
      <Stats />
      <About />
      <Services />
      <BusinessLines />
      <Team />
      <Contact />
    </main>
  );
}
